import { app } from "./app"
import { languages } from "./locale"
import "./types.blocks"

export function shiftHexColorDown(hexColor) {
  hexColor = hexColor?.replace('#', '')
  if (!hexColor?.length) { return }

  let shiftedColor = [...hexColor].map(digit => {
    let newDigit = parseInt(digit, 16) - 2

    if (newDigit < 0) newDigit += 16

    return newDigit.toString(16)
  }).join('')

  return `#${shiftedColor.toUpperCase()}`
}

export const getTailwindStyles = (content) => {
  return {
    bgColorClass: `bg-[${content.bgcolor}]`,
    textColorClass: `text-[${content.textcolor}]`,
    fontWeightClass: `font-${content.fontweight === 400 ? 'normal' : 'bold'}`,
  }
}

export const getLocaleByZendesk = (locale: string) => {
  if (!locale) return undefined
  const matchedLocale = Object.entries(languages).find(
    ([key]) => key.toLowerCase() === locale.toLowerCase()
  )
  if (matchedLocale) {
    return matchedLocale[0]
  }
  const foundEntry = Object.entries(languages).find(
    ([, value]) => value.zendesk?.toLowerCase() === locale.toLowerCase()
  )
  return foundEntry ? foundEntry[0] : undefined
}

export const getLanguageKey = (locale?: string): string | undefined => {
  if (!locale) return undefined
  return Object.keys(languages).find(
    (key) => key.toLowerCase() === locale.toLowerCase()
  )
}

export const getSupportedLocales = (): string[] => {
  const locales = new Set<string>()
  const defaultKey = getLanguageKey(app.defaultLocale)
  if (defaultKey) locales.add(defaultKey)
  app.locales?.forEach((entry: { locale?: string }) => {
    const key = getLanguageKey(entry?.locale)
    if (key) locales.add(key)
  })
  return Array.from(locales)
}

export const buildLocalePath = (locale: string, pathname: string): string => {
  const urlLocale = languages[locale]?.zendesk?.toLowerCase() ?? locale.toLowerCase()
  const localePattern = /^\/(hc\/)?([a-z]{2,3}(?:-[a-z]{2,3})?)(\/|$)/i

  if (localePattern.test(pathname)) {
    return pathname.replace(localePattern, (_, hcPrefix, _currentLocale, trailingSlash) => {
      const prefix = hcPrefix || ''
      return `/${prefix}${urlLocale}${trailingSlash}`
    })
  }

  return `/${urlLocale}${pathname.startsWith('/') ? '' : '/'}${pathname}`
}

export const generateHreflangTag = (type?: "articles" | "services" | "categories", name?: Record<string, string>, id?: string | number) => {
  if (!name) {
    return app.locales.map((locale) => {
      const languageCode = languages[locale.locale]?.code || locale?.locale?.split('-')[0]
      const languageLocale = languages[locale.locale]?.zendesk || locale?.locale?.toLowerCase()
      const url = `${process.env.NEXT_PUBLIC_SITE_URL}/${languageLocale}${type ? "/" + type : ""}`
      return { hreflang: languageCode, href: url }
    })
  } else {
    return Object.entries(name).map(([locale, translation]) => {
      const languageCode = languages[locale]?.code || locale.split('-')[0]
      const languageLocale = languages[locale]?.zendesk || locale.toLowerCase()
      const url = `${process.env.NEXT_PUBLIC_SITE_URL}/${languageLocale}/${type}/${id}`
      return { hreflang: languageCode, href: url }
    })
  }
}

export const isEqual = (value1, value2) => {
  if (value1 === value2) return true
  if (value1 == null || value2 == null) return false
  if (typeof value1 !== typeof value2) return false

  if (Array.isArray(value1) && Array.isArray(value2)) {
    if (value1.length !== value2.length) return false
    for (let i = 0; i < value1.length; i++) {
      if (!isEqual(value1[i], value2[i])) return false
    }
    return true
  }

  if (typeof value1 === 'object' && typeof value2 === 'object') {
    const keys1 = Object.keys(value1)
    const keys2 = Object.keys(value2)
    if (keys1.length !== keys2.length) return false

    for (const key of keys1) {
      if (!keys2.includes(key) || !isEqual(value1[key], value2[key])) {
        return false
      }
    }
    return true
  }
  return false
}

export function needsProviderCatalog(content: Block[]): boolean {
  return content.some(
    (block) =>
      block.type === "services" &&
      !(block as BlockServices).components?.hideproviders,
  )
}

export function isExternalLink(link?: string | null): boolean {
  if (!link) return false
  return /^https?:\/\//i.test(link)
}

export function menuContainsExternalLinks(
  items?: { link?: string; children?: { link?: string; children?: unknown[] }[] }[] | null,
): boolean {
  if (!items?.length) return false
  return items.some(
    (item) =>
      isExternalLink(item.link) ||
      menuContainsExternalLinks(item.children as typeof items),
  )
}

export function formatMenuTitle(title: string, uppercase: boolean): string {
  return uppercase ? title.toUpperCase() : title
}

export function normalizeLinkPath(link: string): string {
  try {
    const url = new URL(link)
    const path = url.pathname.replace(/\/+$/, "") || ""
    return `${url.origin}${path}`.toLowerCase()
  } catch {
    return link.replace(/\/+$/, "").toLowerCase()
  }
}

export function groupLinksByUrlHierarchy(
  items: { title?: LocalizableText; link?: string }[],
): {
  title?: LocalizableText
  link?: string
  children?: {
    title?: LocalizableText
    link?: string
  }[]
}[] {
  const validItems = items.filter(
    (item): item is { title?: LocalizableText; link: string } =>
      Boolean(item.link),
  )

  const itemsByPath = new Map<string, { title?: LocalizableText; link: string }[]>()
  for (const item of validItems) {
    const key = normalizeLinkPath(item.link)
    const group = itemsByPath.get(key) || []
    group.push(item)
    itemsByPath.set(key, group)
  }

  const uniquePaths = Array.from(itemsByPath.keys()).sort(
    (a, b) => a.length - b.length,
  )

  const childPathsByParent = new Map<string, string[]>()
  const parentPathByChild = new Map<string, string>()

  for (const path of uniquePaths) {
    let parentPath: string | null = null
    for (const candidate of uniquePaths) {
      if (candidate === path) continue
      if (
        path.startsWith(`${candidate}/`) &&
        (!parentPath || parentPath.length < candidate.length)
      ) {
        parentPath = candidate
      }
    }
    if (parentPath) {
      parentPathByChild.set(path, parentPath)
      const children = childPathsByParent.get(parentPath) || []
      children.push(path)
      childPathsByParent.set(parentPath, children)
    }
  }

  const menuChildren: {
    title?: LocalizableText
    link?: string
    children?: { title?: LocalizableText; link?: string }[]
  }[] = []

  for (const path of uniquePaths) {
    if (parentPathByChild.has(path)) continue

    const group = itemsByPath.get(path) || []
    const childPaths = childPathsByParent.get(path) || []

    if (childPaths.length === 0) {
      for (const item of group) {
        menuChildren.push({ title: item.title, link: item.link })
      }
      continue
    }

    if (group.length > 1) {
      for (const item of group.slice(0, -1)) {
        menuChildren.push({ title: item.title, link: item.link })
      }
    }

    const parentItem = group[group.length - 1]
    menuChildren.push({
      title: parentItem.title,
      link: parentItem.link,
      children: childPaths.flatMap((childPath) => {
        const childGroup = itemsByPath.get(childPath) || []
        return childGroup.map((item) => ({
          title: item.title,
          link: item.link,
        }))
      }),
    })
  }

  return menuChildren
}

const EARTH_RADIUS_KM = 6371
const KM_TO_MILES = 0.621371

export function getDistanceKm(
  fromLat: number,
  fromLng: number,
  toLat: number,
  toLng: number,
): number {
  const toRadians = (value: number) => (value * Math.PI) / 180
  const latDelta = toRadians(toLat - fromLat)
  const lngDelta = toRadians(toLng - fromLng)
  const fromLatRad = toRadians(fromLat)
  const toLatRad = toRadians(toLat)
  const a =
    Math.sin(latDelta / 2) ** 2 +
    Math.cos(fromLatRad) * Math.cos(toLatRad) * Math.sin(lngDelta / 2) ** 2

  return EARTH_RADIUS_KM * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

export function formatServiceDistance(
  userLocation: UserLocation,
  serviceLocation?: number[],
  useMiles = false,
): string | null {
  if (!serviceLocation || serviceLocation.length < 2) return null

  const serviceLat = Number(serviceLocation[0])
  const serviceLng = Number(serviceLocation[1])
  if (!Number.isFinite(serviceLat) || !Number.isFinite(serviceLng)) return null

  const distanceKm = getDistanceKm(
    userLocation.latitude,
    userLocation.longitude,
    serviceLat,
    serviceLng,
  )

  if (useMiles) {
    const miles = distanceKm * KM_TO_MILES
    if (miles < 0.1) return "< 0.1 mi"
    if (miles < 10) return `${miles.toFixed(1)} mi`
    return `${Math.round(miles)} mi`
  }

  if (distanceKm < 0.1) return "< 0.1 km"
  if (distanceKm < 10) return `${distanceKm.toFixed(1)} km`
  return `${Math.round(distanceKm)} km`
}

type UserLocation = {
  latitude: number
  longitude: number
}

/**
 * Where a "services" nav item should go.
 *
 * When the homepage renders a `services` block, the services already live on
 * the page, so the link scrolls to that block (`#service-map`) instead of
 * navigating away -- the same pattern the "about" item uses. Sites with no
 * services block fall back to the standalone /service-map route.
 *
 * Returns a path, so callers can pass it to <Link> or navigate().
 */
export function servicesMenuTarget(locale: string): string {
  const base = `/${locale.toLowerCase()}`
  const hasServicesBlock = (app.page.content || []).some(b => b.type === "services")
  return hasServicesBlock ? `${base}#service-map` : `${base}/service-map`
}
